import $ from 'jquery';
import AjaxList from './ajax-list/ajax-lists';

/**
 * Cascading Select Handler for Add/Edit Form (Form Tambah)
 * Automatically updates child dropdowns when parent dropdown changes.
 * Uses dedicated data-add-cascade attributes to completely isolate from filters:
 * - data-add-cascade-child: Selector of the child dropdown
 * - data-add-cascade-url: API URL to fetch child data (expects ?parent_id=... or similar)
 * - data-add-cascade-param: Name of the ID parameter (e.g., id_up3)
 */
export function initCascadingSelectFormTambah(container = document) {
  const $container = $(container);
  if (!$container.length) return;

  const selector =
    '[data-add-cascade-child], [data-add-cascade-child-2], [data-add-cascade-child-3]';

  $container
    .find(selector)
    .add($container.filter(selector))
    .each(function () {
      const $parent = $(this);
      if ($parent.data('add-cascading-init')) return;
      $parent.data('add-cascading-init', true);

      const suffixes = ['', '-2', '-3'];
      const configs = [];

      suffixes.forEach((suffix) => {
        const attrBase = 'data-add-cascade-child' + suffix;
        const childSelector = $parent.attr(attrBase);
        const ajaxUrl = $parent.attr('data-add-cascade-url' + suffix);
        const paramName = $parent.attr('data-add-cascade-param' + suffix);
        const valueKey = $parent.attr('data-add-value-key' + suffix);
        const textKey = $parent.attr('data-add-text-key' + suffix);

        if (childSelector && typeof childSelector === 'string' && ajaxUrl) {
          const $child = $(childSelector.trim());
          if ($child.length) {
            configs.push({
              $child: $child,
              url: ajaxUrl,
              param: paramName || 'parent_id',
              valueKey: valueKey,
              textKey: textKey,
            });
          }
        }
      });

      if (configs.length === 0) return;

      // Use a flag specifically for THIS parent to prevent infinite loops
      let isProcessing = false;

      $parent.on('change', async function (e) {
        if (isProcessing) return;

        const parentValue = $parent.val();

        for (const config of configs) {
          const { $child, url, param, valueKey, textKey } = config;
          if (!$child.length || !url) continue;

          isProcessing = true;
          try {
            resetChild($child);

            if (!parentValue || (Array.isArray(parentValue) && parentValue.length === 0)) {
              continue;
            }

            showLoading($child);

            // Use standardized AjaxList.fetch
            const data = await AjaxList.fetch(url, { [param]: parentValue });
            if (data && Array.isArray(data)) {
              populateSelect($child, data, { valueKey, textKey });
            }
          } catch (error) {
            console.error('[CascadingSelectFormTambah] Error for child:', $child.attr('id'), error);
          } finally {
            hideLoading($child);
            isProcessing = false;
            $child.trigger('cascadingComplete');
          }
        }
      });
    });

  // Clean up cache when modal is hidden
  $(document).on('hidden.bs.modal', '.modal', function () {
    const $modal = $(this);
    // Find all cascading children and reset them
    $modal.find(selector).each(function () {
      const $parent = $(this);
      const suffixes = ['', '-2', '-3'];
      suffixes.forEach((suffix) => {
        const childSelector = $parent.attr('data-add-cascade-child' + suffix);
        if (childSelector) {
          const $child = $(childSelector);
          if ($child.length) {
            resetChild($child);
          }
        }
      });
      // Also reset the parent if it's an add-cascade element
      $parent.val(null).trigger('change.select2');
    });
  });
}

function resetChild($el) {
  if (!$el || !$el.length) return;

  $el.empty();

  // Only append an empty option for single selects. Multiple selects use the data-placeholder attribute.
  if (!$el.prop('multiple')) {
    const placeholder =
      $el.attr('data-placeholder') ||
      $el.data('placeholder') ||
      $el.attr('placeholder') ||
      '-- Pilih --';
    $el.append($('<option>', { value: '', text: placeholder }));
  }

  if ($el.val() !== '' && $el.val() !== null) {
    $el.val(null).trigger('change');
  }
}

function showLoading($el) {
  if (!$el || !$el.length) return;
  $el.prop('disabled', true);

  if ($el.hasClass('select2-hidden-accessible')) {
    const $container = $el.next('.select2-container');
    $container.find('.select2-selection').addClass('bg-light opacity-75');

    const loadingText = 'Sedang memuat...';
    $el.empty().append($('<option>', { value: '', text: loadingText }));
    $el.trigger('change.select2');
  }
}

function hideLoading($el) {
  if (!$el || !$el.length) return;
  $el.prop('disabled', false);

  if ($el.hasClass('select2-hidden-accessible')) {
    const $container = $el.next('.select2-container');
    $container.find('.select2-selection').removeClass('bg-light opacity-75');
    $el.trigger('change.select2');
  }
}

/**
 * Populate a select element with data.
 */
function populateSelect($el, data, options = {}) {
  if (!$el || !$el.length || !Array.isArray(data)) return;

  $el.empty();

  // Only append an empty option for single selects
  if (!$el.prop('multiple')) {
    const placeholder =
      $el.attr('data-placeholder') ||
      $el.data('placeholder') ||
      $el.attr('placeholder') ||
      '-- Pilih --';
    $el.append($('<option>', { value: '', text: placeholder }));
  }

  const preferredValKey =
    options.valueKey ||
    $el.attr('data-add-value-key') ||
    $el.data('add-value-key') ||
    $el.attr('data-value-key') ||
    $el.data('value-key');
  const preferredTextKey =
    options.textKey ||
    $el.attr('data-add-text-key') ||
    $el.data('add-text-key') ||
    $el.attr('data-text-key') ||
    $el.data('text-key');

  data.forEach((item) => {
    if (item && typeof item === 'object') {
      let val = preferredValKey ? item[preferredValKey] : undefined;
      if (val === undefined) {
        val =
          item.id ??
          item.id_penyulang ??
          item.id_segment ??
          item.id_percabangan ??
          item.id_kantor_jaga ??
          item.id_pegawai ??
          item.id_jenis ??
          item.id_gardu ??
          item.id_tiang ??
          item.id_ulp ??
          item.id_up3;
        if (val === undefined) {
          const foundKey = Object.keys(item).find(
            (k) => k.startsWith('id_') || k === 'id' || k.startsWith('kode_')
          );
          if (foundKey) val = item[foundKey];
        }
      }

      let txt = preferredTextKey ? item[preferredTextKey] : undefined;
      if (txt === undefined) {
        txt =
          item.nama ??
          item.nama_penyulang ??
          item.nama_segment ??
          item.nama_percabangan ??
          item.nama_kantor_jaga ??
          item.nama_pegawai ??
          item.nama_jenis ??
          item.nama_gardu ??
          item.nama_tiang ??
          item.nama_ulp ??
          item.nama_up3 ??
          item.text ??
          item.label;
        if (txt === undefined) {
          const foundKey = Object.keys(item).find(
            (k) =>
              k.startsWith('nama_') ||
              k === 'nama' ||
              k === 'name' ||
              k === 'text' ||
              k === 'label' ||
              k.startsWith('kode_')
          );
          if (foundKey) txt = item[foundKey];
        }
      }

      if (val !== undefined && txt !== undefined) {
        const $option = $('<option>', { value: val, text: txt });
        if (item.latitude !== undefined && item.latitude !== null && item.latitude != 0) {
          $option.attr('data-latitude', item.latitude);
        }
        if (item.longitude !== undefined && item.longitude !== null && item.longitude != 0) {
          $option.attr('data-longitude', item.longitude);
        }
        $el.append($option);
      }
    }
  });

  $el.trigger('change');
}
